/**
 * AssessmentCyclesCard (P222) -- a Home tile showing the completion of
 * the current (open) framework assessment cycles, by COVERAGE (controls
 * graded / total) + an on-time vs overdue indicator.
 *
 * Lives in framework/ because it reads the framework endpoint; mounted
 * into Home's adminSlot column via App.tsx (the module-boundary seam --
 * shared/ can't import framework/). Not exec-gated: any framework user.
 * Self-hides when there are no open cycles; degrades quietly on error.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { BrassLink } from "shared/components/BrassLink";
import { Card } from "shared/components/Card";
import { StatusPill } from "shared/components/StatusPill";
import { useCycleProgress } from "framework/api/assessment_hooks";
import type { CycleProgress } from "shared/api/types";

function CycleRow({ c }: { c: CycleProgress }): JSX.Element {
  const { t } = useTranslation();
  const pct = Math.max(0, Math.min(100, c.coverage_pct));
  return (
    <Link
      to="/framework/assessments"
      data-testid={`assessment-cycle-${c.id}`}
      className="block rounded-md px-1 py-1.5 hover:bg-paper"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-[13px] font-medium text-ink">{c.name}</span>
        {c.is_overdue ? (
          <StatusPill tone="red">
            {t("modules.framework.cycles.overdue", { days: Math.abs(c.days_to_due) })}
          </StatusPill>
        ) : (
          <span className="shrink-0 font-mono text-[10px] text-ink-muted">
            {t("modules.framework.cycles.dueIn", { days: c.days_to_due })}
          </span>
        )}
      </div>
      <div className="mt-1 flex items-center gap-2">
        <span
          className="h-[5px] flex-1 overflow-hidden rounded-full bg-paper-2"
          aria-hidden="true"
        >
          <span
            className={`block h-full ${c.is_overdue ? "bg-rag-amber" : "bg-brand-brass"}`}
            style={{ width: `${pct}%` }}
          />
        </span>
        <span className="shrink-0 font-mono text-[11px] text-ink-2 tabular-nums">
          {c.controls_graded}/{c.controls_total} · {pct}%
        </span>
      </div>
      <div className="mt-0.5 flex items-baseline justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-muted">
          {c.framework_code}
        </span>
        <span
          data-testid={`assessment-cycle-signoff-${c.id}`}
          className="shrink-0 font-mono text-[10px] text-ink-muted tabular-nums"
        >
          {t("modules.framework.cycles.signoff", {
            count: c.controls_signed_off,
            pct: Math.max(0, Math.min(100, c.signoff_pct)),
          })}
        </span>
      </div>
    </Link>
  );
}

export function AssessmentCyclesCard(): JSX.Element | null {
  const { t } = useTranslation();
  // Round 19 I24 -- it was a component literal.
  const MICRO = t("modules.framework.cycles.micro");
  const query = useCycleProgress();

  // No framework access (403) / error / loading / no open cycles -> render
  // nothing (Home stays clean; this is a supplementary signal).
  if (!query.data || query.data.length === 0) return null;

  const cycles = query.data.slice(0, 3); // the most-pressing few

  return (
    <Card testid="assessment-cycles-card" className="space-y-1">
      <header className="flex items-baseline justify-between gap-2">
        <p className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-muted">
          <MarsadIcon name="framework" size={13} className="text-brand-brassDark" />
          {MICRO}
        </p>
        <BrassLink to="/framework/assessments" className="text-[12.5px]">
          {t("modules.framework.cycles.open")}
        </BrassLink>
      </header>
      <div className="divide-y divide-paper-2">
        {cycles.map((c) => (
          <CycleRow key={c.id} c={c} />
        ))}
      </div>
    </Card>
  );
}
